import React, { useContext, useEffect, useState } from 'react'
import {
  borderRadius,
  Box,
  CanvasProvider,
  Card,
  Flex,
  FormField,
  FormFieldLabelPosition,
  Menu,
  Modal,
  PrimaryButton,
  space,
  StatusIndicator,
  StatusIndicatorEmphasis,
  StatusIndicatorType,
  SystemIcon,
  Table,
  TableRow,
  TertiaryButton,
  Text,
  TextArea,
  Tooltip,
  useAssistiveHideSiblings,
  useDisableBodyScroll,
  useFocusTrap,
  useInitialFocus,
  useModalModel,
  useReturnFocus
} from '@workday/canvas-kit-react'
import {
  chevronRightIcon,
  editIcon,
  objectIcon,
  relatedActionsVerticalIcon,
  sortIcon,
  trashIcon
} from '@workday/canvas-system-icons-web'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { CenteredLayout } from '../../components/CenteredLayout'
import { PPLoadingDots } from '../../components/PPLoadingDots'
import FeedConfiguration from '../models/FeedConfiguration'
import {
  closeIncident,
  deleteIncidentPost,
  fetchIncident,
  fetchIncidentJSON,
  markAddressPrivate,
  markAddressPublic,
  reopenIncident,
  submitIncidentPost,
  suppressFromAll,
  suppressFromPublic
} from '../models/WebAppRequests'
import { MenuTheme, PrimaryButtonColors } from '../../components/CanvasThemes'
import { EmptyState } from '../../components/EmptyState'
import { ScreenWidthCategoryContext, ScreenWidthCategory } from '../../utils/ScreenWidthCategoryContext'
import { RespondAppConfiguration } from '../models/RespondAppConfiguration'
import { imageURLWithSize } from '../../models/ImageURL'
import { AgencyModel } from '../../models/AgencyModel'
import { IncidentModel } from '../../models/IncidentModel'
import { UnitStatus, unitStatusColor, unitStatusDescription } from '../../models/UnitStatus'
import { coordinateIsInvalid } from '../../models/Coordinate'
import { prettyDate, daysAgoDate } from '../../models/PrettyDate'
import { CognitoUserSession } from 'amazon-cognito-identity-js'
import IncidentPostsCard from './IncidentPostsCard'
import NewIncidentPostCard from './NewIncidentPostCard'
import { isIncidentAdminForAgency, UpdateConfirmationContext } from '../../utils'
import styled from '@emotion/styled'

export type IncidentDetailsProps = {
  appConfiguration: RespondAppConfiguration
  configuration: FeedConfiguration
  incidentID: string
  agency: AgencyModel
  session?: CognitoUserSession
  onSelectAgency?: (agency: AgencyModel) => void
}

type JSONState = {
  loading: boolean
  response: any
  show: boolean
}

const StyledCard = styled(Card)`
  :hover {
    background: #efefef;
  }
`
const StyledFlex = styled(Flex)`
  :hover {
    background: #efefef;
  }
`
const StyledModalCard = styled(Modal.Card)`
  width: unset !important;
`

const IncidentDetails = ({
  appConfiguration,
  configuration,
  incidentID,
  agency,
  session,
  onSelectAgency
}: IncidentDetailsProps) => {
  const updateConfirmation = useContext(UpdateConfirmationContext)
  const screenWidthCategory = useContext(ScreenWidthCategoryContext)
  const labelPosition =
    screenWidthCategory <= ScreenWidthCategory.ExtraSmall ? FormFieldLabelPosition.Top : FormFieldLabelPosition.Left
  const [incident, setIncident] = useState<IncidentModel | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [reverseNarrative, setReverseNarrative] = useState(false)
  const [showHistory, setShowHistory] = useState(true)
  const [json, setJSON] = useState<JSONState>({
    loading: false,
    response: null,
    show: false
  })

  const refreshIncident = (incidentID: string, fromHistory = false) => {
    setLoading(true)
    setShowHistory(!fromHistory)
    return fetchIncident(incidentID, configuration.environment, session)
      .then(incident => setIncident(incident))
      .catch(error => {
        //TODO
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    refreshIncident(incidentID)
  }, [incidentID])

  const categories = ['DP', 'ER', 'OS', 'TR', 'TA', 'AR'] as UnitStatus[]

  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY as string
  })

  const narrative = reverseNarrative ? incident?.narrative.slice().reverse() ?? [] : incident?.narrative ?? []
  const jsonViewerModel = useModalModel()
  useInitialFocus(jsonViewerModel)
  useReturnFocus(jsonViewerModel)
  useFocusTrap(jsonViewerModel)
  useAssistiveHideSiblings(jsonViewerModel)
  useDisableBodyScroll(jsonViewerModel)
  jsonViewerModel.state.visibility = 'visible'

  const isIncidentAdmin = appConfiguration.context === 'ADMIN' && isIncidentAdminForAgency(session, agency.agencyID)
  const showJSONButton = isIncidentAdmin || appConfiguration.context === 'SANDBOX_LINK'

  const isClosed = incident?.closedDateTime !== undefined
  const isSuppressedFromPublic = incident?.suppressInApp === true

  const isNonMedicalType =
    incident &&
    incident.callType.id !== 'ME' &&
    incident.callType.id !== 'LA' &&
    incident.callType.id !== 'IFT' &&
    incident.callType.id !== 'CP' &&
    incident.callType.id !== 'CPR'
  const showStreetView = isNonMedicalType || appConfiguration.context === 'ADMIN'

  return loading ? (
    <PPLoadingDots />
  ) : incident ? (
    <CenteredLayout>
      {json.show && json.response && (
        <Modal model={jsonViewerModel}>
          <Modal.Overlay>
            <StyledModalCard>
              <Modal.Body>
                <Flex flexDirection="column" gap={space.xs} alignItems="center">
                  <TextArea readOnly grow value={json.response} style={{ height: '400px', minWidth: 'calc(60vw)' }} />
                  <Flex flexDirection="row" gap={space.xs}>
                    <PrimaryButton
                      onClick={event => navigator.clipboard.writeText(incident.json)}
                      colors={PrimaryButtonColors}
                    >
                      Copy
                    </PrimaryButton>
                    <Modal.CloseButton
                      aria-label="Close"
                      onClick={event => setJSON(prev => ({ ...prev, show: false }))}
                    >
                      Close
                    </Modal.CloseButton>
                  </Flex>
                </Flex>
              </Modal.Body>
            </StyledModalCard>
          </Modal.Overlay>
        </Modal>
      )}
      <Flex flexDirection="column" gap={space.xs}>
        <Box>
          <Flex flexDirection="row" gap={space.xs}>
            <Flex.Item flex={1} />
            {showJSONButton &&
              (json.loading ? (
                <PPLoadingDots />
              ) : (
                <Tooltip title="View JSON">
                  <TertiaryButton
                    icon={objectIcon}
                    onClick={event => {
                      if (json.loading) {
                        return
                      }

                      setJSON(prev => ({ ...prev, loading: true }))
                      fetchIncidentJSON(
                        incident.recordID,
                        configuration.token,
                        configuration.environment,
                        session
                      ).then(value =>
                        setJSON({
                          loading: false,
                          response: value,
                          show: true
                        })
                      )
                    }}
                  />
                </Tooltip>
              ))}
            {isIncidentAdmin && (
              <CanvasProvider theme={MenuTheme}>
                <Menu
                  onSelect={data => {
                    if (data.id === 'reopen-incident') {
                      updateConfirmation.setConfirmation({
                        title: '',
                        message: 'Are you sure you want to reopen this incident?',
                        onCancel: () => {},
                        onConfirm: () => {
                          reopenIncident(
                            incident.incidentNumber,
                            incident.agencyID,
                            configuration.environment,
                            session
                          ).then(data => {
                            refreshIncident(incidentID)
                          })
                        }
                      })
                    } else if (data.id === 'close-incident') {
                      updateConfirmation.setConfirmation({
                        title: '',
                        message: 'Are you sure you want to close this incident?',
                        onCancel: () => {},
                        onConfirm: () => {
                          closeIncident(
                            incident.incidentNumber,
                            incident.agencyID,
                            configuration.environment,
                            session
                          ).then(data => {
                            refreshIncident(incidentID)
                          })
                        }
                      })
                    } else if (data.id === 'remove-public') {
                      updateConfirmation.setConfirmation({
                        title: '',
                        message: 'Are you sure you want to remove this incident from the public feed?',
                        onCancel: () => {},
                        onConfirm: () => {
                          suppressFromPublic(
                            incident.incidentNumber,
                            incident.agencyID,
                            configuration.environment,
                            session
                          ).then(data => {
                            refreshIncident(incidentID)
                          })
                        }
                      })
                    } else if (data.id === 'delete-incident') {
                      updateConfirmation.setConfirmation({
                        title: '',
                        message: 'Are you sure you want to delete this incident?',
                        onCancel: () => {},
                        onConfirm: () => {
                          suppressFromAll(
                            incident.incidentNumber,
                            incident.agencyID,
                            configuration.environment,
                            session
                          ).then(data => {
                            window.history.back()
                          })
                        }
                      })
                    }
                  }}
                >
                  <Menu.Target as={TertiaryButton} icon={relatedActionsVerticalIcon} />
                  <Menu.Popper>
                    <Menu.Card>
                      <Menu.List>
                        {!isClosed && (
                          <Menu.Item data-id="close-incident">
                            <Menu.Item.Text>Close Incident</Menu.Item.Text>
                          </Menu.Item>
                        )}
                        {isClosed && (
                          <Menu.Item data-id="reopen-incident">
                            <Menu.Item.Text>Reopen Incident</Menu.Item.Text>
                          </Menu.Item>
                        )}
                        {!isSuppressedFromPublic && (
                          <Menu.Item data-id="remove-public">
                            <Menu.Item.Text>Remove From Public Feed</Menu.Item.Text>
                          </Menu.Item>
                        )}
                        {(!isClosed || !isSuppressedFromPublic) && <Menu.Divider />}
                        <Menu.Item data-id="delete-incident">
                          <Menu.Item.Icon icon={trashIcon} />
                          <Menu.Item.Text>Delete Incident</Menu.Item.Text>
                        </Menu.Item>
                      </Menu.List>
                    </Menu.Card>
                  </Menu.Popper>
                </Menu>
              </CanvasProvider>
            )}
          </Flex>
        </Box>
        {coordinateIsInvalid(incident.locationCoordinate) && isMapLoaded && (
          <Card padding={0}>
            <GoogleMap
              onLoad={map => {
                const circle = new google.maps.Circle({
                  center: incident.locationCoordinate,
                  radius: 0.025 * 1600
                })
                map.fitBounds(circle.getBounds()!)
              }}
              mapContainerStyle={{
                height: '400px',
                width: '100%',
                borderRadius: borderRadius.l
              }}
              options={{
                rotateControl: false,
                panControl: false,
                streetViewControl: showStreetView,
                scaleControl: false
              }}
            >
              <Marker
                options={{
                  icon: {
                    url: incident.mapPinIconURL(),
                    scaledSize: new google.maps.Size(incident.mapPinIconSize(), incident.mapPinIconSize())
                  }
                }}
                position={incident.locationCoordinate}
              />
            </GoogleMap>
          </Card>
        )}
        <Card>
          <Flex flexDirection="column" gap={space.s}>
            <Flex flexDirection="row" gap={space.xs} alignItems="center">
              <Text typeLevel="heading.small">{incident.callTypeDescription()}</Text>
              <StatusIndicator
                emphasis={StatusIndicatorEmphasis.Low}
                label={incident.archived ? 'Archived' : incident.closedDateTime ? 'Closed' : 'Active'}
                type={StatusIndicatorType.Gray}
              />
            </Flex>

            {incident.agencyIncidentCallTypeDescription && (
              <Text typeLevel="body.medium">{incident.agencyIncidentCallTypeDescription}</Text>
            )}
            <Flex flexDirection="row" alignItems="center" gap={space.xxs}>
              <img src={incident.mapPinIconURL()} style={{ width: 48, height: 48 }} />
              <Flex flexDirection="column">
                {incident.commonPlaceName && (
                  <Text typeLevel="body.small" fontWeight="medium">
                    {incident.commonPlaceName}
                  </Text>
                )}
                <Text whiteSpace="pre-line" typeLevel="body.small">
                  {incident.formattedAddress()}
                </Text>
                {isIncidentAdmin && appConfiguration.showPublicPrivateOverrideControl && (
                  <Flex flexDirection="row" alignContent="center" alignItems="center" gap={space.xs}>
                    <Text whiteSpace="pre-line" typeLevel="body.small">
                      {incident.publicLocation ? 'Public Address' : 'Private Address'}
                    </Text>
                    <CanvasProvider theme={MenuTheme}>
                      <Menu
                        onSelect={data => {
                          if (data.id === 'mark-private') {
                            updateConfirmation.setConfirmation({
                              title: '',
                              message: 'Are you sure you want to mark this address as private?',
                              onCancel: () => {},
                              onConfirm: () => {
                                markAddressPrivate(
                                  incident.incidentNumber,
                                  incident.agencyID,
                                  configuration.environment,
                                  session
                                ).then(data => {
                                  refreshIncident(incidentID)
                                })
                              }
                            })
                          } else if (data.id === 'mark-public') {
                            updateConfirmation.setConfirmation({
                              title: '',
                              message: 'Are you sure you want to mark this address as public?',
                              onCancel: () => {},
                              onConfirm: () => {
                                markAddressPublic(
                                  incident.incidentNumber,
                                  incident.agencyID,
                                  configuration.environment,
                                  session
                                ).then(data => {
                                  refreshIncident(incidentID)
                                })
                              }
                            })
                          }
                        }}
                      >
                        <Menu.Target as={TertiaryButton} icon={editIcon} />
                        <Menu.Popper>
                          <Menu.Card>
                            <Menu.List>
                              {incident.publicLocation && (
                                <Menu.Item data-id="mark-private">
                                  <Menu.Item.Text>Mark Address Private</Menu.Item.Text>
                                </Menu.Item>
                              )}
                              {!incident.publicLocation && (
                                <Menu.Item data-id="mark-public">
                                  <Menu.Item.Text>Mark Address Public</Menu.Item.Text>
                                </Menu.Item>
                              )}
                            </Menu.List>
                          </Menu.Card>
                        </Menu.Popper>
                      </Menu>
                    </CanvasProvider>
                  </Flex>
                )}
              </Flex>
            </Flex>
            {incident.formattedTacticalChannel() && (
              <Text typeLevel="subtext.large">{incident.formattedTacticalChannel()}</Text>
            )}
            {incident.formattedPriority() && <Text typeLevel="subtext.large">{incident.formattedPriority()}</Text>}
            {incident.alarmLevelDescription() && (
              <Text typeLevel="subtext.large">{incident.alarmLevelDescription()}</Text>
            )}
          </Flex>
        </Card>
        {isIncidentAdmin && (
          <NewIncidentPostCard
            incidentID={incident.recordID}
            onSubmit={(data, callback) => {
              const incidentPost = { ...data, incidentID: incidentID }
              submitIncidentPost(incidentPost, configuration.environment, session)
                .then(data => {
                  refreshIncident(incidentID).then(() => callback(true))
                })
                .catch(reason => {
                  callback(false)
                })
            }}
          />
        )}
        {incident.posts.length > 0 && (
          <IncidentPostsCard
            posts={incident.posts}
            canEditPosts={isIncidentAdmin}
            timeZone={agency.timeZone}
            onSubmit={(data, callback) => {
              const incidentPost = { ...data, incidentID: incidentID }
              submitIncidentPost(incidentPost, configuration.environment, session)
                .then(data => {
                  refreshIncident(incidentID).then(() => callback(true))
                })
                .catch(reason => {
                  callback(false)
                })
            }}
            onDelete={(data, callback) => {
              deleteIncidentPost(data.id ?? '', incidentID, configuration.environment, session)
                .then(data => refreshIncident(incidentID).then(() => callback(true)))
                .catch(reason => {
                  callback(false)
                })
            }}
          />
        )}
        <Card>
          {onSelectAgency ? (
            <StyledFlex
              flexDirection="row"
              gap={space.xs}
              alignItems="center"
              cursor="pointer"
              onClick={() => onSelectAgency(agency)}
            >
              {agency.hasAgencyImage() && (
                <img
                  src={imageURLWithSize(agency.agencyImageURI, 60, 60, 1)}
                  srcSet={`${imageURLWithSize(agency.agencyImageURI, 60, 60, 2)} 2x`}
                />
              )}
              <Text typeLevel="body.large" fontWeight="bold">
                {agency.shortName}
              </Text>
              <Flex flex={1} />
              <SystemIcon icon={chevronRightIcon} />
            </StyledFlex>
          ) : (
            <Flex flexDirection="row" gap={space.xs} alignItems="center">
              {agency.hasAgencyImage() && (
                <img
                  src={imageURLWithSize(agency.agencyImageURI, 60, 60, 1)}
                  srcSet={`${imageURLWithSize(agency.agencyImageURI, 60, 60, 2)} 2x`}
                />
              )}
              <Text typeLevel="body.large" fontWeight="bold">
                {agency.shortName}
              </Text>
            </Flex>
          )}
        </Card>
        {incident.units.length > 0 && (
          <Card paddingBottom={space.xxxs}>
            <Card.Heading>Assigned Units</Card.Heading>
            {categories
              .filter(unitStatus => incident.formattedUnits(unitStatus) !== '')
              .map(unitStatus => (
                <FormField label={unitStatusDescription(unitStatus)} labelPosition={labelPosition} key={unitStatus}>
                  <Text whiteSpace="pre-line" typeLevel="body.small" color={unitStatusColor(unitStatus)}>
                    {incident.formattedUnits(unitStatus)}
                  </Text>
                </FormField>
              ))}
          </Card>
        )}
        {(incident.incidentNumber ||
          incident.agencyIncidentCallType ||
          incident.agencyDeterminantCode ||
          incident.jurisdiction ||
          incident.commandName ||
          incident.firstDueStation ||
          incident.locationComment) && (
          <Card paddingBottom={space.xxxs}>
            <Card.Heading>Additional Information</Card.Heading>
            {incident.incidentNumber && (
              <FormField label="Agency Incident Number" labelPosition={labelPosition}>
                <Text whiteSpace="pre-line" typeLevel="body.small">
                  {incident.incidentNumber}
                </Text>
              </FormField>
            )}
            {incident.agencyIncidentCallType && (
              <FormField label="Agency Incident Call Type" labelPosition={labelPosition}>
                <Text whiteSpace="pre-line" typeLevel="body.small">
                  {incident.agencyIncidentCallType}
                </Text>
              </FormField>
            )}
            {incident.agencyDeterminantCode && (
              <FormField label="Agency Determinant Code" labelPosition={labelPosition}>
                <Text whiteSpace="pre-line" typeLevel="body.small">
                  {incident.agencyDeterminantCode}
                </Text>
              </FormField>
            )}
            {incident.jurisdiction && (
              <FormField label="Jurisdiction" labelPosition={labelPosition}>
                <Text whiteSpace="pre-line" typeLevel="body.small">
                  {incident.jurisdiction}
                </Text>
              </FormField>
            )}
            {incident.commandName && (
              <FormField label="Command Name" labelPosition={labelPosition}>
                <Text whiteSpace="pre-line" typeLevel="body.small">
                  {incident.commandName}
                </Text>
              </FormField>
            )}
            {incident.firstDueStation && (
              <FormField label="First Due Station" labelPosition={labelPosition}>
                <Text whiteSpace="pre-line" typeLevel="body.small">
                  {incident.firstDueStation}
                </Text>
              </FormField>
            )}
            {incident.locationComment && (
              <FormField label="Location Comment" labelPosition={labelPosition}>
                <Text whiteSpace="pre-line" typeLevel="body.small">
                  {incident.locationComment}
                </Text>
              </FormField>
            )}
          </Card>
        )}
        <Card>
          <Table>
            <tbody>
              {incident.closedDateTime && (
                <TableRow>
                  <td>
                    <Text whiteSpace="nowrap" typeLevel="subtext.large">
                      {prettyDate(incident.closedDateTime, agency.timeZone, true, true)}
                    </Text>
                  </td>
                  <td>
                    <Text typeLevel="subtext.large" fontWeight="bold">{`Closed (${incident.formattedElapsedTime(
                      false
                    )})`}</Text>
                  </td>
                </TableRow>
              )}
              {incident.onSceneDateTime && (
                <TableRow>
                  <td>
                    <Text whiteSpace="nowrap" typeLevel="subtext.large">
                      {prettyDate(incident.onSceneDateTime, agency.timeZone, true, true)}
                    </Text>
                  </td>
                  <td>
                    <Text typeLevel="subtext.large" fontWeight="bold">
                      Arrived
                    </Text>
                  </td>
                </TableRow>
              )}
              <TableRow>
                <td>
                  <Text whiteSpace="nowrap" typeLevel="subtext.large">
                    {prettyDate(incident.callReceivedDateTime, agency.timeZone, true, true)}
                  </Text>
                </td>
                <td>
                  <Text typeLevel="subtext.large" fontWeight="bold">
                    Received
                  </Text>
                </td>
              </TableRow>
            </tbody>
          </Table>
        </Card>
        <Card paddingBottom={space.xxxs}>
          <FormField label="PulsePoint ID" labelPosition={labelPosition}>
            <Text typeLevel="body.small">{incident.recordID}</Text>
          </FormField>
        </Card>
        {(incident.submissionDelay !== undefined || incident.dispatchSubmissionDelay !== undefined) && (
          <Card paddingBottom={space.xxxs}>
            <Card.Heading>Performance Stats</Card.Heading>
            <FormField label="Submission Delay" labelPosition={labelPosition}>
              <Text typeLevel="body.small">
                {incident.submissionDelay === undefined
                  ? 'N/A'
                  : incident.submissionDelay === 1
                  ? '1 second'
                  : `${incident.submissionDelay} seconds`}
              </Text>
            </FormField>
            <FormField label="Dispatch Submission Delay" labelPosition={labelPosition}>
              <Text typeLevel="body.small">
                {incident.dispatchSubmissionDelay === undefined
                  ? 'N/A'
                  : incident.dispatchSubmissionDelay === 1
                  ? '1 second'
                  : `${incident.dispatchSubmissionDelay} seconds`}
              </Text>
            </FormField>
          </Card>
        )}
        {narrative.length > 0 && (
          <Card>
            <Card.Heading>
              <Flex flexDirection="row" columnGap={space.xs} alignItems="center">
                Narrative
                <Tooltip title="Reverse Order">
                  <TertiaryButton
                    icon={sortIcon}
                    onClick={() => {
                      setReverseNarrative(prev => !prev)
                    }}
                  />
                </Tooltip>
              </Flex>
            </Card.Heading>
            <Table>
              <tbody>
                {narrative.map((item, index) => (
                  <TableRow key={index}>
                    <td>
                      <Text whiteSpace="nowrap">{item.formattedDateTime(agency.timeZone)}</Text>
                    </td>
                    <td>
                      <Text whiteSpace="pre-line" fontWeight="bold">
                        {item.text}
                      </Text>
                    </td>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
        {showHistory && incident.locationHistory.length > 0 && (
          <>
            <Card>
              <Card.Heading>
                <Flex flexDirection="row" columnGap={space.xs} alignItems="center">
                  Recent Location History
                </Flex>
              </Card.Heading>
              {incident.locationHistory.map((item, index) => (
                <Tooltip title="Click to view Incident">
                  <StyledCard
                    key={'i' + index}
                    padding={space.xxs}
                    onClick={() => refreshIncident(item.recordID, true)}
                    marginBottom={space.xxs}
                  >
                    <Text whiteSpace="pre-line" fontWeight="bold">
                      {item.callTypeDescription()}
                    </Text>
                    <br />
                    <Text fontSize="0.85em" whiteSpace="nowrap" fontStyle="italic">
                      {daysAgoDate(item.callReceivedDateTime, agency.timeZone, true).map((c: string, i: number) => {
                        if (i == 0) return <>{c}</>
                        else
                          return (
                            <span>
                              <br />
                              {c}
                            </span>
                          )
                      })}
                    </Text>
                  </StyledCard>
                </Tooltip>
              ))}
            </Card>
          </>
        )}
      </Flex>
    </CenteredLayout>
  ) : (
    <EmptyState>
      <EmptyState.Heading>Failed to load incident details</EmptyState.Heading>
    </EmptyState>
  )
}

export default IncidentDetails
